import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  appliedPlaylistRemovals,
  authPlaylistReadPreferred,
  playlistCountAfterRemovals,
  playlistEditable,
  playlistPageForIndex,
  playlistWalk,
  withoutPlaylistEntries,
  PLAYLIST_FIRST_PAGE,
  PLAYLIST_HEADER_PROBE_PAGE,
  type Playlist,
  type PlaylistEntry,
  type QueuePlaylistWindow,
} from '@visious/core';
import { fetchAuthPlaylistPage, fetchPlaylistPage } from '../../api/playlists';
import type { ProblemCopy } from '../../api/problem';
import { getPlatform } from '../../platform/instance';
import { useSession } from '../../platform/session';
import { describeWriteProblem } from '../../playlists/model';
import { useEntryMutations } from '../../playlists/mutations';
import { setPlaylistReversed, usePlaylistReversed } from '../../playlists/orderMemory';
import { authPlaylistKey } from '../../playlists/useOwnedPlaylists';
import { clearQueue, projectPlaylistQueue, readQueue } from '../../queue/store';
import { useCopy } from '../../strings/useCopy';
import { toQueueLinkedRows, WATCH_PLAYLIST_PAGE_SIZE, type WatchPlaylistRow } from './model';

/**
 * Turning a list around, which the two lists this panel draws do differently enough that one field
 * describing both would have to lie about one of them.
 *
 * A **playlist** can only be flipped in the display: this API has no reorder endpoint, so the entries
 * keep their real positions and the choice is remembered per playlist in this browser
 * (`playlists/orderMemory.ts`), shared with the playlist page's own Order menu. It is a *state*, so the
 * control that sets it is a switch that reads as on.
 *
 * A **queue of the reader's own** is ours, so a reverse really moves the rows and the panel then draws
 * them in that order. There is nothing left over to remember afterwards, so the control is an *act*
 * rather than a switch: pressing it again turns the list back because reversing twice is the identity,
 * and a lit face would start lying at the next edit, since a video appended to a flipped queue is at
 * its end. `queueActs` (`core/queue/model.ts`) is where that difference is decided.
 */
export type WatchListOrder =
  | {
      readonly kind: 'display';
      readonly reversed: boolean;
      readonly setReversed: (reversed: boolean) => void;
    }
  | { readonly kind: 'reorder'; readonly reverse: () => void };

/**
 * What the side panel draws, whichever list is in it.
 *
 * There is one binding and one panel, because there is one list: a `list` in the address makes the
 * queue a window on that playlist (`core/queue/model.ts`'s `QueueLink`), so the rows are always the
 * queue's rows and the fields below are always about the queue. What differs between the two lists is
 * only what is behind them — a playlist has pages to read, a run to fill and a removal a server can
 * refuse, while a list of the reader's own has none of those and answers them flatly
 * (`queue/useQueuePanel.ts` builds this from the queue plus {@link WatchListLoad}).
 */
export interface WatchPlaylistBinding {
  /**
   * The whole list as it stands, **in display order** — reversed when the reader has flipped this
   * playlist, so that nothing downstream has to remember to flip it again. Empty while the page holding
   * the playing entry is still on the wire.
   */
  readonly rows: readonly WatchPlaylistRow[];
  readonly title: string | null;
  /**
   * Where the panel's heading leads: the playlist page itself, or `null` for a list of the reader's own.
   *
   * A queue nobody else can read has no page of its own — it lives in this panel — so there is nowhere
   * for its heading to go and the panel draws the title as plain text instead of as a link.
   */
  readonly href: string | null;
  /** "451 of 575", or `null` while the list is playing nothing. */
  readonly positionText: string | null;
  /** The first page is still on the wire; the panel draws its heading and nothing else. */
  readonly loading: boolean;
  /** A page the run or the pager asked for is on the wire. */
  readonly loadingMore: boolean;
  /** One sentence, or `null`. A panel that cannot load is a panel that says so and stays. */
  readonly problem: string | null;
  /** A refused removal — kept apart from `problem`, which is about the list not arriving. */
  readonly entryProblem: ProblemCopy | null;
  /**
   * Whether the rows may carry a remove button. `queueActs` answers it: a list of the reader's own is
   * always editable, and a linked one follows the playlist — an `IVPL…` list this account owns, with a
   * session to write with.
   */
  readonly canRemove: boolean;
  /** Remove one row, addressed by its `removeKey`. Applied at once, then refetched where it is sent. */
  readonly removeEntry: (removeKey: string) => void;
  /** Which rows have a removal in flight — the spinner face, keyed by `removeKey`. */
  readonly removingKeys: ReadonlySet<string>;
  /**
   * The rows' up and down arrows, or `null` where there are none: a linked queue (a playlist has no
   * reorder endpoint, `queueActs`) and a list of one row, where neither arrow could ever do anything.
   */
  readonly move: WatchListMove | null;
  /** What auto-advance plays when this video ends — `null` at the end of the list. */
  readonly next: WatchPlaylistRow | null;
  /** The row before the playing one, as shown — `null` at the start of the list. */
  readonly previous: WatchPlaylistRow | null;
  /** How this list's order can be turned around, which is not the same act for the two lists. */
  readonly order: WatchListOrder;
  /** The window the panel opens on: the one holding the playing row. */
  readonly initialPage: number;
  /** The list runs on past the last row in hand; the pager's Next asks for the rest. */
  readonly hasMore: boolean;
  readonly loadMore: () => void;
  /**
   * The list runs on *before* the first row in hand — which an anchored run does by construction, since
   * opening a playlist at entry 350 leaves 350 entries in front of it unread. The pager's Prev at the
   * first window asks for them.
   */
  readonly hasEarlier: boolean;
  readonly loadEarlier: () => void;
}

/**
 * The paged read behind a linked queue: the playlist the address names, projected into the queue store.
 *
 * **It produces no rows.** The panel draws the queue and nothing else, so this hook's whole output is
 * the projection plus the handful of facts a panel cannot get from an array in this tab: whether a page
 * is on the wire, whether the read failed, whether the run can still grow at either end, and what a
 * removal did.
 *
 * The two endpoints, the fold and the query keys are the playlist page's own — `usePlaylist.ts`
 * documents why the pages have to be deduplicated by `index` and why the two endpoints are separate
 * cache entries — so opening a playlist page and then a video from it costs no second fetch of
 * anything, in either display order.
 *
 * **Opening at a position costs one request.** The route's `index` is the walk's anchor, so the run is
 * the contiguous stretch *around* it (`playlistWalk`) and the first request is the page holding it
 * ({@link playlistPageForIndex}). Reading from an end instead would fetch every page between the
 * playlist's first entry and the playing one, which on a 7 271-entry uploads playlist is dozens of
 * ~250 KB responses.
 *
 * **The run grows on demand, in two ways.** The pager asks — forward past the last row and backward
 * before the first — and the run also fills itself while the cursor is within one window of the end the
 * list plays towards. That second one is not a courtesy: without it a video ending on the last loaded
 * row advances the cursor off the run and the queue reports itself finished with hundreds of entries
 * still unread.
 */
/** Moving one row a place, and the two rows that cannot go any further that way. */
export interface WatchListMove {
  readonly act: (key: string, by: -1 | 1) => void;
  readonly firstKey: string;
  readonly lastKey: string;
}

export interface WatchListLoad {
  /** The playlist the address names, or `null` when it names none. */
  readonly playlistId: string | null;
  /** The playlist's own title, before there is a link carrying it. `null` until the header arrives. */
  readonly title: string | null;
  readonly loading: boolean;
  readonly loadingMore: boolean;
  readonly problem: string | null;
  readonly entryProblem: ProblemCopy | null;
  /**
   * Remove one row of the playlist, addressed by its **queue key**. The playlist's own removal key is
   * looked up here, because this is the only place that knows both vocabularies.
   */
  readonly removeEntry: (key: string) => void;
  /** Queue keys with a removal in flight. */
  readonly removingKeys: ReadonlySet<string>;
  /** The playlist runs on past the display's last row. */
  readonly hasMore: boolean;
  readonly loadMore: () => void;
  /** The playlist runs on before the display's first row. */
  readonly hasEarlier: boolean;
  readonly loadEarlier: () => void;
  /** The display flip, remembered per playlist. */
  readonly order: WatchListOrder;
}

const NO_ENTRIES: readonly PlaylistEntry[] = [];
const NO_PAGES: readonly Playlist[] = [];
const NO_PAGE_PARAMS: readonly number[] = [];
const NO_KEYS: readonly string[] = [];
const NO_QUEUE_KEYS: ReadonlySet<string> = new Set();
const NO_REACH = { before: false, after: false } as const;

interface Reach {
  readonly before: boolean;
  readonly after: boolean;
}

/** Carries a refusal through react-query's `unknown` error channel — never a rendered string (R9). */
class PlaylistPageError extends Error {}

export function useWatchPlaylist(input: {
  /** The route's `list`. */
  readonly playlistId: string | null;
  /** The route's `index`, which the walk anchors the run on. */
  readonly index: number | null;
  readonly videoId: string;
  /** `false` on /embed, which draws no list and must touch no queue either. */
  readonly enabled: boolean;
  /** The panel is shown. Hidden, the paged read stands down, so a hidden panel costs no requests. */
  readonly shown: boolean;
}): WatchListLoad {
  const { playlistId, index, videoId, enabled, shown } = input;
  const session = useSession();
  const words = useCopy().text.watch.playlist;
  const grantKey = session.grantedAtEpochSec ?? 0;
  const authenticated = authPlaylistReadPreferred(
    playlistId ?? '',
    session.ready && session.facts.signedIn,
  );
  const reversed = usePlaylistReversed(playlistId ?? '');
  const reading = enabled && shown && playlistId !== null;

  const loadPage = useCallback(
    async (page: number): Promise<Playlist> => {
      const boot = await getPlatform();
      if (!boot.ok || playlistId === null) throw new PlaylistPageError('no platform');
      const result = authenticated
        ? // `null` scopes on a read: the instance is the authority (`useSubscriptions.ts`).
          await fetchAuthPlaylistPage(boot.platform, playlistId, page, null)
        : await fetchPlaylistPage(boot.platform, playlistId, page);
      if (!result.ok) throw new PlaylistPageError('refused');
      return result.value;
    },
    [authenticated, playlistId],
  );

  /**
   * Entries this session removed, applied locally until the refetch lands — the playlist page's state,
   * for the playlist page's reason: `withoutPlaylistEntries` **renumbers**, because the server does, so
   * a row merely filtered out would leave every later position one too high and the numbers are drawn
   * on screen. Applying it to a list that has already dropped the row is a no-op, so the list can
   * safely outlive the invalidation the mutation fires.
   */
  const [removed, setRemoved] = useState<readonly string[]>(NO_KEYS);
  const [removingKeys, setRemovingKeys] = useState<ReadonlySet<string>>(NO_QUEUE_KEYS);
  const [entryProblem, setEntryProblem] = useState<ProblemCopy | null>(null);
  const entries = useEntryMutations();

  /**
   * What the pager has asked for, as the number of pages in hand at the moment of the press.
   *
   * A press is one page, and this is what makes it one: the ask holds only while the count is unchanged,
   * so the page that lands answers it and the reach turns itself off. A count rather than a flag because
   * a flag needs somebody to clear it, and the only honest moment to do that is when a response arrives.
   */
  const [askedEarlierAt, setAskedEarlierAt] = useState<number | null>(null);
  const [askedLaterAt, setAskedLaterAt] = useState<number | null>(null);

  /**
   * Which way the run has to grow, in the **playlist's** directions rather than the screen's — the
   * pager's asks and the lookahead together. Computed from the pages a caller hands it rather than from
   * this render's, so `getNextPageParam` and the panel's own pager read one answer.
   */
  const reachFor = useCallback(
    (pages: readonly Playlist[], requested: readonly number[]): Reach => {
      const run = playlistWalk(pages, { backward: reversed, requested, anchor: index });
      const asked = askedReach(reversed, {
        earlier: askedEarlierAt === pages.length,
        later: askedLaterAt === pages.length,
      });
      const ahead = lookaheadReach(run.entries, cursorIn(run.entries, videoId, index), reversed);
      return { before: asked.before || ahead.before, after: asked.after || ahead.after };
    },
    [askedEarlierAt, askedLaterAt, index, reversed, videoId],
  );

  const pages = useInfiniteQuery({
    queryKey:
      authenticated && playlistId !== null
        ? authPlaylistKey(playlistId, grantKey)
        : ['playlist', playlistId ?? ''],
    /*
     * The page holding the position the address named, which is the whole of "opening at entry 350
     * costs one request". Without a position there is nothing to aim at, so it is the first page
     * forwards and the header-only probe when the display is flipped — the probe carries the count the
     * page arithmetic needs for ~1.5 KB where page 1 costs ~250 KB (§7g).
     */
    initialPageParam:
      index !== null
        ? playlistPageForIndex(index)
        : reversed
          ? PLAYLIST_HEADER_PROBE_PAGE
          : PLAYLIST_FIRST_PAGE,
    queryFn: ({ pageParam }) => loadPage(pageParam),
    getNextPageParam: (
      _last: Playlist,
      all: readonly Playlist[],
      _lastParam: number,
      allParams: readonly number[],
    ) =>
      playlistWalk(all, {
        backward: reversed,
        requested: allParams,
        anchor: index,
        reach: reachFor(all, allParams),
      }).nextPage,
    retry: false,
    staleTime: 5 * 60 * 1000,
    // `session.ready` for `usePlaylist`'s reason: before it there is no answer to *which endpoint*,
    // and guessing the public one 404s every private playlist on arrival and caches that.
    enabled: reading && session.ready,
  });

  const loaded = pages.data?.pages ?? null;
  // Positionally matched to `loaded` — see `usePlaylist.ts` for why the narrowing is a formality.
  const requestedPages = useMemo(
    () =>
      (pages.data?.pageParams ?? NO_PAGE_PARAMS).filter(
        (page): page is number => typeof page === 'number',
      ),
    [pages.data],
  );

  /**
   * The run, in the playlist's own order and with this session's removals applied. The reversal into
   * display order happens on the way into the queue, so that the cursor, the neighbours, the panel and
   * the corner player all read one order and cannot disagree with what is on screen.
   */
  const run = useMemo(
    () =>
      loaded === null
        ? NO_ENTRIES
        : withoutPlaylistEntries(
            playlistWalk(loaded, { backward: reversed, requested: requestedPages, anchor: index }),
            removed,
          ).entries,
    [index, loaded, removed, requestedPages, reversed],
  );

  const header = loaded?.[0] ?? null;
  // Every applied removal comes off the header's own count, so "3 of 11" does not go on claiming 12.
  const total = playlistCountAfterRemovals(
    header?.videoCount ?? null,
    appliedPlaylistRemovals(loaded, removed),
  );
  const at = cursorIn(run, videoId, index);

  /**
   * Whether the playlist has rows past each end of the **display**, which is what decides whether the
   * pager offers a step there at all. A run read from an end already sits against one of the list's own
   * ends, so the direction it cannot grow in is the one the pager must not offer: the page the walk
   * would name there is the forward one, and pressing Prev would page the reader further away from
   * where they are.
   */
  const first = run[0]?.index ?? null;
  const last = run[run.length - 1]?.index ?? null;
  const beforeList = first !== null && first > 0;
  const pastListKnown = total !== null && last !== null && last < total - 1;
  // A list that declares no length has no end to compare against — a YouTube mix carries none (§7g) —
  // so the walk's own stop rule is the whole answer there.
  const pastList = total === null || pastListKnown;
  const growthEnd = reversed ? beforeList : pastList;
  const growthStart = reversed ? pastListKnown : beforeList;

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = pages;
  const reach = useMemo(
    () => reachFor(loaded ?? NO_PAGES, requestedPages),
    [loaded, reachFor, requestedPages],
  );

  /**
   * The page that would grow each end of the display, asked for one direction at a time. The pager's two
   * buttons need to know which end can still move, and the walk's own combined answer cannot say: it
   * names one page, and with both directions open that is whichever core serves first.
   */
  const growth = useMemo(() => {
    if (loaded === null) return { start: null as number | null, end: null as number | null };
    const walk = (reach: Reach): number | null =>
      playlistWalk(loaded, {
        backward: reversed,
        requested: requestedPages,
        anchor: index,
        reach,
      }).nextPage;
    return {
      start: walk(askedReach(reversed, { earlier: true, later: false })),
      end: walk(askedReach(reversed, { earlier: false, later: true })),
    };
  }, [index, loaded, requestedPages, reversed]);

  /*
   * One page at a time, and only while something is waiting for it: the run has not reached the playing
   * row's window yet, or a reach is on. An effect rather than a loop inside the query, because each step
   * has to see the *folded* result to know whether it is done, and because a reader who never reaches
   * this playlist's far end must not pay for the pages between.
   *
   * "Whole" rather than merely "found", and the difference is one request at one boundary: the walk
   * stops at the API page holding the playing entry, and when that entry is the *last* row of it the
   * opening window is a single row with three blanks where the rest of the playlist should be — seen on
   * a 457-entry playlist opened at index 450, which drew one row under "76 / 76".
   *
   * The position is the row's in the **display**, which is the run read backwards when the reader has
   * flipped this playlist. Measured in the playlist's own direction instead, a flipped list opened at its
   * last entry would want a window past every row it holds and go on reading pages until it had the whole
   * playlist — which is the one thing reading backwards exists to avoid.
   */
  const displayAt = at === -1 ? -1 : reversed ? run.length - 1 - at : at;
  const windowEnd =
    displayAt === -1
      ? 0
      : (Math.floor(displayAt / WATCH_PLAYLIST_PAGE_SIZE) + 1) * WATCH_PLAYLIST_PAGE_SIZE;
  const settled = at !== -1 && run.length >= windowEnd;
  /*
   * `settled` has to be its own gate rather than being left to `hasNextPage`, because a run read from an
   * **end** names its next page whether or not anybody asked for one: an address with a `list` and no
   * position is read from the playlist's start until the playing row turns up, and without this the same
   * rule would go on to read the other 7 000 entries behind it.
   */
  const wanted = !settled || reach.before || reach.after;

  useEffect(() => {
    // `loaded === null` rather than an empty run: the flipped walk's first response is the header-only
    // probe page, which carries no entries at all and must not read as "nothing to walk".
    if (!reading || loaded === null || pages.isError) return;
    if (!wanted || !hasNextPage || isFetchingNextPage) return;
    void fetchNextPage();
  }, [
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    loaded,
    pages.isError,
    reading,
    wanted,
  ]);

  /**
   * Whether this build can write to the playlist: an `IVPL…` list this account owns, with a session to
   * write with. Both, for `usePlaylist`'s reason — an `IVPL…` list read while signed out is still not
   * one this build can change. It travels on the link, so every control that asks what may be done to
   * the list asks `queueActs` and not this hook.
   */
  const editable =
    header !== null &&
    playlistEditable(header, session.facts.username) &&
    session.ready &&
    session.facts.signedIn;

  /** The window the queue is to become. `null` until the playlist's header has arrived. */
  const window = useMemo((): QueuePlaylistWindow | null => {
    if (playlistId === null || header === null) return null;
    const rows = toQueueLinkedRows(run);
    return {
      link: { playlistId, title: header.title, videoCount: total, editable },
      rows: reversed ? [...rows].reverse() : rows,
      currentIndex: index,
      currentVideoId: videoId,
    };
  }, [editable, header, index, playlistId, reversed, run, total, videoId]);

  /*
   * The projection, which is this hook's whole output as far as the panel is concerned. An effect, never
   * a render: a store write during a render is a write two components could race for.
   */
  useEffect(() => {
    if (!enabled || playlistId === null) return;
    /*
     * The panel is hidden, so the paged read has stood down and this playlist will not be read at all.
     * A playlist the app has not read cannot be the list, and a stale queue standing beside it is worse
     * than none: the pill that brings the panel back is still there, and showing it reads the playlist.
     */
    if (!shown) {
      clearQueue();
      return;
    }
    projectPlaylistQueue(playlistId, window);
  }, [enabled, playlistId, shown, window]);

  /**
   * One row of the playlist, addressed by the key the panel draws — the queue's. The playlist's own
   * removal key comes off the live queue rather than the run, because the row is a queue row and the
   * live snapshot is what every act on the queue reads (`queue/store.ts`).
   *
   * The playing row is removable, and taking it out moves the queue on to its successor — which §7i's
   * renumbering puts at the same `index`, so the projection lands the cursor there by itself.
   */
  const removeEntry = useCallback(
    (key: string) => {
      if (playlistId === null || removingKeys.has(key)) return;
      const indexId = readQueue().entries.find((entry) => entry.key === key)?.indexId ?? null;
      if (indexId === null) return;
      setEntryProblem(null);
      setRemovingKeys((previous) => new Set(previous).add(key));
      void (async () => {
        const result = await entries.remove(playlistId, indexId);
        if (result.ok) setRemoved((previous) => [...previous, indexId]);
        else setEntryProblem(describeWriteProblem(result.problem, 'remove'));
        setRemovingKeys((previous) => {
          const next = new Set(previous);
          next.delete(key);
          return next;
        });
      })();
    },
    [entries, playlistId, removingKeys],
  );

  const pageCount = loaded?.length ?? 0;

  return {
    playlistId,
    title: header?.title ?? null,
    // Still nothing to show. Flipped, the first response is the header-only probe, and an anchored run
    // is empty until the page holding its own index lands — so the request that brings the first row is
    // a *next* page, and `isPending` alone would leave the panel drawing an empty list with nothing
    // saying why.
    loading: reading && (pages.isPending || (run.length === 0 && (isFetchingNextPage || hasNextPage))),
    // A page the run wanted and a page the pager asked for look the same from here, and they read the
    // same to the reader too.
    loadingMore: isFetchingNextPage,
    problem: pages.isError ? words.problem : null,
    entryProblem,
    removeEntry,
    removingKeys,
    hasMore: growthEnd && growth.end !== null,
    loadMore: useCallback(() => setAskedLaterAt(pageCount), [pageCount]),
    hasEarlier: growthStart && growth.start !== null,
    loadEarlier: useCallback(() => setAskedEarlierAt(pageCount), [pageCount]),
    order: {
      kind: 'display',
      reversed,
      setReversed: useCallback(
        (next: boolean) => {
          if (playlistId !== null) setPlaylistReversed(playlistId, next);
        },
        [playlistId],
      ),
    },
  };
}

/**
 * Where the playing row is in the run, or `-1` while the run does not hold it.
 *
 * The video first, because that is what is playing, and the position when the run holds no such video —
 * which is what a route left naming a row somebody removed is asking for, since §7i's renumbering puts
 * the successor at exactly that `index`. `placeCursor` (`core/queue/model.ts`) settles the queue's own
 * cursor the same way, so the two agree about which row this is.
 */
function cursorIn(
  entries: readonly PlaylistEntry[],
  videoId: string,
  index: number | null,
): number {
  const byVideo = entries.findIndex((entry) => entry.video.videoId === videoId);
  if (byVideo !== -1 || index === null) return byVideo;
  return entries.findIndex((entry) => entry.index === index);
}

/**
 * The direction the run fills itself in: the one the list plays towards, while the cursor is within one
 * window of the end of what has been read.
 *
 * It is not optional and it is not the pager's job. A video ending on the last loaded row advances the
 * cursor off the run, and a queue whose cursor has run off the end reports itself finished — so without
 * this a 575-entry playlist ends at row 202 with a box in the corner saying the list is over. It turns
 * itself off when the page lands, because the cursor is then no longer near the edge.
 *
 * Mapped to the **playlist's** directions rather than the screen's: flipped, further on in the list is
 * `before`.
 */
function lookaheadReach(
  entries: readonly PlaylistEntry[],
  at: number,
  reversed: boolean,
): Reach {
  if (at === -1) return NO_REACH;
  const near = reversed
    ? at < WATCH_PLAYLIST_PAGE_SIZE
    : at >= entries.length - WATCH_PLAYLIST_PAGE_SIZE;
  if (!near) return NO_REACH;
  return reversed ? { before: true, after: false } : { before: false, after: true };
}

/** The pager's two directions, in the playlist's terms. Flipped, its Prev asks for higher indices. */
function askedReach(
  reversed: boolean,
  asked: { readonly earlier: boolean; readonly later: boolean },
): Reach {
  return reversed
    ? { before: asked.later, after: asked.earlier }
    : { before: asked.earlier, after: asked.later };
}
